import React, { useState } from 'react';
import styled from 'styled-components';
import cx from 'classnames';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { useSpring, animated } from 'react-spring';
import { AspectRatio } from 'react-aspect-ratio';
import { useSitecoreContext } from 'stores/sitecoreContext';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import { getMobileOS } from 'utils/getMobileOS';
import { shareThis, getUrl } from 'utils/shareThis';
import useFavorites from 'utils/hooks/useFavorites';
import { track } from 'utils/tracking';
import trackingEvents from 'utils/trackingEvents';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';
import {
  videoTranscriptFileUrl,
  videoTranscriptFileExtension,
} from 'utils/videoTranscriptFileUrl';
import breakpoints from 'utils/breakpoints';
import { isValidUrl } from 'utils/validateUrlSrc';
import { isValidTitle } from 'utils/validateTitle';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';
import { Section, Container } from 'components/shared';
import { Copy } from 'components/components/atoms';
import { ImageCarousel } from 'components/components/molecules';

import loadable from '@loadable/component';
const ChevronDown = loadable(() => import('components/icons/ChevronDown'));
const ChatIcon = loadable(() => import('components/icons/ChatBubble'));
const MailIcon = loadable(() => import('components/icons/Mail'));
const ShareIcon = loadable(() => import('components/icons/Share'));
const HeartFilled = loadable(() => import('components/icons/HeartFilled'));
const HeartIcon = loadable(() => import('components/icons/Heart'));
const PhotoIconGallery = loadable(() =>
  import('components/icons/PhotoIconGallery'),
);
const TourIconGallery = loadable(() =>
  import('components/icons/TourIconGallery'),
);
const VideoIconGallery = loadable(() =>
  import('components/icons/VideoIconGallery'),
);

const StyledSection = styled(Section)`
  padding-top: 0 !important;
  ${({ isMobile }) =>
    isMobile
      ? 'padding-bottom: 0 !important;'
      : 'padding-bottom: 0.625rem !important;'}
`;

const StyledHeading = styled.h2`
  ${({ hasCopy }) => hasCopy && 'margin-bottom: 0rem;'}
  font-size: 1.625rem;
  @media ${breakpoints.lg} {
    font-size: 2.25rem;
  }
  @media ${breakpoints.xl} {
    font-size: 2.875rem;
  }
`;

const StyledCopy = styled(Copy)`
  @media ${breakpoints.lg} {
    width: 40rem;
  }
`;

const StaticCarouselContainer = styled.div`
  ${({ isMobile }) => !isMobile && 'height: auto;'}
`;

const Header = styled.div`
  height: 3.75rem;

  ${({ showNewSchemaColor, newSchemaColorHexaCode }) =>
    showNewSchemaColor ? `background-color: ${newSchemaColorHexaCode};` : ''}

  @media ${breakpoints.md} {
    min-height: 3.75rem;
  }
`;

const FiltersMedia = styled.div`
  height: 2.25rem;
`;

const FiltersMediaButton = styled.button`
  ${({ isTour }) => isTour && 'width: 7.25rem;'}

  ${({ showNewSchemaColor, newSchemaTextColorHexaCode }) =>
    showNewSchemaColor ? `border-color: ${newSchemaTextColorHexaCode};` : ''}
  ${({ showNewSchemaColor, newSchemaTextColorHexaCode, isFilterSelected }) =>
    showNewSchemaColor &&
    isFilterSelected &&
    `background-color: ${newSchemaTextColorHexaCode};`}
  ${({ showNewSchemaColor, newSchemaColorHexaCode, isFilterSelected }) =>
    showNewSchemaColor &&
    !isFilterSelected &&
    `background-color: ${newSchemaColorHexaCode};`}
`;
const FilterMediaIconContainer = styled.div`
  ${({ showNewSchemaColor, isFilterSelected, newSchemaColorHexaCode }) =>
    showNewSchemaColor &&
    isFilterSelected &&
    `color: ${newSchemaColorHexaCode};`}
  ${({ showNewSchemaColor, isFilterSelected }) =>
    !showNewSchemaColor && isFilterSelected && 'color: white;'}
`;
const FilterMediaTextContainer = styled.span`
  ${({ showNewSchemaColor, isFilterSelected, newSchemaColorHexaCode }) =>
    showNewSchemaColor &&
    isFilterSelected &&
    `color: ${newSchemaColorHexaCode};`}
  ${({ showNewSchemaColor, isFilterSelected }) =>
    !showNewSchemaColor && isFilterSelected && 'color: white;'}
`;

const ShareIconsContainer = styled.div`
  ${({ setMobileTop }) => setMobileTop && 'top: -1.1rem;'}
`;

const IconContainer = styled.div`
  width: 2.25rem;
  height: 2.25rem;
`;

const StyledImageCarousel = styled(ImageCarousel)`
  height: calc(100% - 3.75rem);
`;

const getFilterLabel = (filterList, selectedFilter) => {
  const filter = filterList.find((e) => e.id === selectedFilter);
  if (filter) return filter.title;
  return selectedFilter;
};

const filterCtas = (media) => {
  if (media && media.length > 0) {
    const ctas = [
      { key: 'tour', text: '360 Tours' },
      { key: 'image', text: 'Photos' },
      { key: 'video', text: 'Videos' },
    ];
    const cleanMedia = media.filter((item) => {
      if (item && item.type) {
        if (item.type === 'tour') {
          return (
            item.src &&
            item.src.includes('iframe') &&
            !/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.src)
          );
        }
        if (item.type === 'image') {
          return item.src && isValidUrl(item.src);
        }
        if (['internal', 'external'].includes(item.type)) {
          return (
            item.src &&
            isValidUrl(item.src) &&
            !/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.src)
          );
        }
      }
      return false;
    });
    const filterCtas = ctas.filter((cta) => {
      const itemIndex = cleanMedia.findIndex(
        (i) =>
          i.type === cta.key ||
          (cta.key === 'video' && ['external', 'internal'].includes(i.type)),
      );
      return itemIndex !== -1;
    });
    let sortCtas = [];
    const hasVideo = filterCtas.find((e) => e.key === 'video');
    if (hasVideo) sortCtas.push(hasVideo);
    const hasTour = filterCtas.find((e) => e.key === 'tour');
    if (hasTour) sortCtas.push(hasTour);
    const hasImage = filterCtas.find((e) => e.key === 'image');
    if (hasImage) sortCtas.push(hasImage);
    return sortCtas;
  }
  return [];
};

const mediaFilters = {
  '360 Tours': 'tour',
  Videos: 'video',
  Photos: 'image',
};

const cleanCarouselItems = (carousel, isMobile) => {
  return carousel.filter((item) => {
    if (item && item.type) {
      if (item.type === 'tour') {
        return (
          item.src &&
          item.src.includes('iframe') &&
          !/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.src)
        );
      }
      if (item.type === 'image') {
        return item.src && isValidUrl(item.src);
      }
      if (['internal', 'external'].includes(item.type)) {
        return (
          item.src &&
          isValidUrl(item.src) &&
          !/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.src)
        );
      }
    }
    if (item && item.image) {
      const imageSrc =
        (isMobile ? item.image.srcMobile : item.image.srcDesktop) ||
        item.image.src;
      return item.image && imageSrc;
    }
  });
};

const GalleryComponent = ({ fields, rendering }) => {
  const {
    showNewSchemaColor,
    newSchemaColorHexaCode,
    newSchemaTextColorHexaCode,
  } = useNewColorSchemaOptions();
  const { favoritesList, saveFavorite, deleteFavorite } = useFavorites();
  const { ContextID = {} } = useSitecoreContext() || {};
  const id = ContextID.value;
  const showFavorites = getFieldValue(fields, 'showFavorites');
  const showShare = getFieldValue(fields, 'showShare');
  const title = getFieldValue(fields, 'title');
  const copy = getFieldValue(fields, 'copy');

  const communityName = getFieldValue(fields, 'name');
  const communityId = getFieldValue(fields, 'id');
  const communityMedia = (getFieldValue(fields, 'media') || []).filter(
    (el) => el.src,
  );
  const plansList = getFieldValue(fields, 'plans');

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const isIOS = getMobileOS() === 'iOS';

  const communityAndPlans = [
    {
      id: communityId,
      title: communityName,
      media: communityMedia,
    },
    ...plansList,
  ];
  const [galleryFilter, setGalleryFilter] = useState(
    communityAndPlans[0] && communityAndPlans[0].id,
  );
  const [mediaFilter, setMediaFilter] = useState();
  // Derived from galleryFilter, not held in state: cleanCarouselItems() filters
  // on isMobile, and useBreakpoint() reports 'xs' until after mount, so a
  // useState initializer would keep the desktop gallery filtered by the mobile
  // rule forever.
  const selectedGroup =
    communityAndPlans.find((x) => x.id === galleryFilter) ||
    communityAndPlans[0];
  const mediaSelected = cleanCarouselItems(
    (selectedGroup && selectedGroup.media) || [],
    isMobile,
  );
  const [resetMedia, setResetMedia] = useState(false);
  const filteredCtas = filterCtas(mediaSelected);

  const [isSharedVisible, setIsSharedVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const filteredMedia = mediaSelected.filter(
    (item) =>
      !mediaFilter ||
      item.type === mediaFilter ||
      (mediaFilter === 'video' && ['internal', 'external'].includes(item.type)),
  );

  const dropdown = useSpring({
    height: open && communityAndPlans ? communityAndPlans.length * 38 : 0,
    delay: open ? 200 : 0,
  });

  const isFavorite = favoritesList.includes(id);

  const [chekBoxFocusTitle, setChekBoxFocusTitle] = useState(0);

  const handleFavoriteClick = (id) => {
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };
  const handleReset = (value) => {
    setResetMedia(value);
  };

  const renderVideoDescription = () =>
    filteredMedia
      .filter((m) => m.type === 'internal' || m.type === 'external')
      .filter(
        (m) =>
          m.alt && m.videoAltTranscriptFile && m.videoAltTranscriptFile.link,
      )
      .map((m, i) => (
        <span
          key={i}
          className='richtext text-left text-md leading-7 font-graphie text-mattamy-blue'
        >
          {'Transcript: '}
          <a
            href={videoTranscriptFileUrl(m.videoAltTranscriptFile)}
            download={
              m.videoAltTranscriptFile.name +
              videoTranscriptFileExtension(m.videoAltTranscriptFile.extension)
            }
            className='underline'
            aria-label={'Download ' + m.alt + ' transcript'}
          >
            {m.alt}
          </a>
        </span>
      ));

  if (!mediaSelected.length) return null;

  return (
    <>
      <StyledSection
        id={'gallery'}
        className='xl:px-20 my-10 select-none'
        isMobile={isMobile}
        tabIndex={'-1'}
      >
        <Container noPadding className='flex flex-col items-center'>
          {isValidTitle(title) && (
            <StyledHeading
              hasCopy={copy ? true : false}
              color={showNewSchemaColor ? 'black' : 'blue'}
              className={cx(
                'font-bold leading-tiny font-trade-gothic-20 mb-6 md:mb-10-mattamy text-center px-4',
                {
                  'text-black': showNewSchemaColor,
                  'text-mattamy-blue ': !showNewSchemaColor,
                },
              )}
            >
              {title}
            </StyledHeading>
          )}
          {copy && (
            <StyledCopy
              color={showNewSchemaColor ? 'black' : 'blue'}
              className='md:text-lg text-center pb-6 px-8'
            >
              {copy}
            </StyledCopy>
          )}

          <div
            //ratio={isMobile ? '375/310' : '1200/650'}
            className='w-full'
          >
            <StaticCarouselContainer className='w-full bg-mattamy-gray-bg md:rounded-lg overflow-auto relative'>
              <Header
                className={cx(
                  'h-20 flex justify-center md:justify-between items-center px-5',
                  {
                    'bg-action-blue': !showNewSchemaColor,
                  },
                )}
                showNewSchemaColor={showNewSchemaColor}
                newSchemaColorHexaCode={newSchemaColorHexaCode}
              >
                <div className='hidden md:flex w-1/4 md:w-1/6 lg:w-1/4'>
                  <FiltersMedia
                    className={cx(
                      'rounded-full flex bg-white uppercase  justify-center items-center text-sm font-trade-gothic-20 cursor-pointer uppercase',
                      {
                        'text-white': showNewSchemaColor,
                        'text-mattamy-blue': !showNewSchemaColor,
                        hidden: !(filteredCtas && filteredCtas.length > 1),
                      },
                    )}
                    role='radiogroup'
                    aria-label='Content type'
                  >
                    {filteredCtas &&
                      filteredCtas.map((item, i) => (
                        <FiltersMediaButton
                          className={cx(
                            'h-full flex flex-row  uppercase border-2  items-center',
                            {
                              'pl-2 rounded-l-full': i === 0,
                              'pr-2 rounded-r-full':
                                i === filteredCtas.length - 1,
                              'border-mattamy-blue bg-mattamy-blue':
                                !showNewSchemaColor &&
                                mediaFilters[item.text] === mediaFilter,
                              'border-mattamy-blue bg-white':
                                !showNewSchemaColor &&
                                mediaFilters[item.text] !== mediaFilter,
                              /* "border-white bg-white":
                                showNewSchemaColor &&
                                mediaFilters[item.text] === mediaFilter,
                              "border-white bg-black":
                                showNewSchemaColor &&
                                mediaFilters[item.text] !== mediaFilter,*/
                            },
                          )}
                          showNewSchemaColor={showNewSchemaColor}
                          isFilterSelected={
                            mediaFilters[item.text] === mediaFilter
                          }
                          newSchemaColorHexaCode={newSchemaColorHexaCode}
                          newSchemaTextColorHexaCode={
                            newSchemaTextColorHexaCode
                          }
                          key={item.text + i}
                          isTour={item.text === '360 Tours'}
                          role='radio'
                          aria-checked={mediaFilters[item.text] === mediaFilter}
                          aria-label={item.text}
                          onClick={(e) => {
                            if (mediaFilters[item.text] === mediaFilter) {
                              setMediaFilter();
                            } else {
                              if (item.text === '360 Tours')
                                trackAnalyticsEvent({
                                  gtagEvent: 'VirtualTourFilterClick',
                                  mouseflowTag: 'VirtualTourFilterClick',
                                });
                              if (item.text === 'Videos')
                                trackAnalyticsEvent({
                                  gtagEvent: 'GalleryVideosClick',
                                  mouseflowTag: 'GalleryVideosClick',
                                });
                              setMediaFilter(mediaFilters[item.text]);
                            }
                            setOpen(false);
                            setChekBoxFocusTitle(item.text);
                          }}
                        >
                          <FilterMediaIconContainer
                            className={cx('pl-2 flex w-8', {
                              'text-white':
                                !showNewSchemaColor &&
                                mediaFilters[item.text] == mediaFilter,
                              'text-white':
                                showNewSchemaColor &&
                                mediaFilters[item.text] !== mediaFilter,
                              'text-mattamy-blue':
                                !showNewSchemaColor &&
                                mediaFilters[item.text] !== mediaFilter,
                            })}
                            showNewSchemaColor={showNewSchemaColor}
                            isFilterSelected={
                              mediaFilters[item.text] === mediaFilter
                            }
                            newSchemaColorHexaCode={newSchemaColorHexaCode}
                            newSchemaTextColorHexaCode={
                              newSchemaTextColorHexaCode
                            }
                          >
                            {item.key === 'video' && (
                              <VideoIconGallery className={'w-8'} />
                            )}
                            {item.key === 'tour' && (
                              <TourIconGallery className={'w-8'} />
                            )}
                            {item.key === 'image' && (
                              <PhotoIconGallery className={'w-8'} />
                            )}
                          </FilterMediaIconContainer>

                          <FilterMediaTextContainer
                            className={cx('ml-1 mt-1 pr-1', {
                              'text-mattamy-blue':
                                !showNewSchemaColor &&
                                mediaFilters[item.text] !== mediaFilter,
                              'text-white':
                                !showNewSchemaColor &&
                                mediaFilters[item.text] === mediaFilter,
                              'text-white':
                                showNewSchemaColor &&
                                mediaFilters[item.text] !== mediaFilter,
                              /* "text-black":
                                showNewSchemaColor &&
                                mediaFilters[item.text] === mediaFilter,*/
                            })}
                            showNewSchemaColor={showNewSchemaColor}
                            isFilterSelected={
                              mediaFilters[item.text] === mediaFilter
                            }
                            newSchemaColorHexaCode={newSchemaColorHexaCode}
                            newSchemaTextColorHexaCode={
                              newSchemaTextColorHexaCode
                            }
                          >
                            {item.text}
                          </FilterMediaTextContainer>
                        </FiltersMediaButton>
                      ))}
                  </FiltersMedia>
                </div>
                <div
                  className='flex flex-wrap relative md:w-4/6 lg:w-1/2 flex justify-center'
                  role='radiogroup'
                  aria-label='Content type'
                >
                  {isMobile ? (
                    <>
                      <div
                        className='inline-block uppercase font-trade-gothic-20 text-white font-semibold cursor-pointer mt-1'
                        onClick={() => setOpen(!open)}
                      >
                        <span className='mr-2'>
                          {getFilterLabel(communityAndPlans, galleryFilter)}
                        </span>
                        <ChevronDown
                          className={cx(
                            'inline-block w-10-mattamy mb-1 transition-all duration-500 transform',
                            {
                              '-rotate-180': open,
                            },
                          )}
                        />
                      </div>
                    </>
                  ) : (
                    communityAndPlans &&
                    communityAndPlans.map((item, i) => (
                      <>
                        <button
                          className='uppercase leading-tight active:outline-none   font-trade-gothic-20 text-white font-semibold'
                          key={item.id + i}
                          onClick={(e) => {
                            setGalleryFilter(item.id);
                            setResetMedia(true);
                            setMediaFilter();
                            setChekBoxFocusTitle(item.title);
                          }}
                          role='radio'
                          aria-checked={galleryFilter === item.id}
                          aria-label={item.title}
                        >
                          <p
                            className={cx('pt-1', {
                              'border-b-2': galleryFilter === item.id,
                            })}
                          >
                            {item.title}
                          </p>
                        </button>
                        {i < communityAndPlans.length - 1 && (
                          <span className='mx-3 leading-10 text-white'>|</span>
                        )}
                      </>
                    ))
                  )}
                  <p
                    aria-live='polite'
                    style={{
                      position: 'absolute',
                      left: '-10000px',
                      top: ' auto',
                      width: '1px',
                      height: '1px',
                      overflow: 'hidden',
                    }}
                  >
                    Slide has changed view to {chekBoxFocusTitle}
                  </p>
                </div>
                <div className='absolute right-0 md:relative md:mr-0 md:mt-0 w-1/4 md:w-1/6 lg:w-1/4 flex justify-end items-center z-20'>
                  {showFavorites && (
                    <div className='absolute right-0 mr-12 md:relative md:mr-0'>
                      <IconContainer
                        className='flex items-center justify-center rounded-full text-white mr-1 md:mr-10-mattamy hover:text-white md:cursor-pointer'
                        onClick={() => handleFavoriteClick(id)}
                        onMouseEnter={() => !isMobile && setFocused(true)}
                        onMouseLeave={() => !isMobile && setFocused(false)}
                        aria-label={
                          isFavorite ? 'Mark as unfavorite' : 'Mark as favorite'
                        }
                        aria-pressed={isFavorite ? 'true' : 'false'}
                      >
                        {isFavorite && <HeartFilled className='h-5 w-5' />}
                        {!isFavorite && focused && (
                          <HeartFilled className='h-5 w-5' />
                        )}
                        {!isFavorite && !focused && (
                          <HeartIcon className='h-5 w-5' />
                        )}
                      </IconContainer>
                    </div>
                  )}
                  {showShare && (
                    <ShareIconsContainer
                      onBlur={() => {
                        setTimeout(() => {
                          setIsSharedVisible(false);
                        }, 100);
                      }}
                      className='absolute right-0 mr-3 md:relative md:mr-0'
                      setMobileTop={isSharedVisible && isMobile}
                    >
                      <IconContainer
                        className={cx(
                          'flex items-center justify-center rounded-full text-white hover:text-white focus:outline-none cursor-pointer',
                          { 'bg-mattamy-blue text-white': isSharedVisible },
                        )}
                        tabIndex='1'
                        onClick={() => {
                          setIsSharedVisible(!isSharedVisible);
                        }}
                      >
                        <ShareIcon
                          className={cx('h-5', {
                            'text-white': isSharedVisible,
                          })}
                        />
                      </IconContainer>
                      {isSharedVisible && (
                        <>
                          <div
                            className='st-custom-button'
                            onClick={() => {
                              shareThis({
                                dataNetwork: 'email',
                                dataUrl: getUrl(),
                              });
                            }}
                          >
                            <IconContainer
                              data-aos='fade-down'
                              data-aos-duration='100'
                              className='absolute rounded-full bg-mattamy-blue mt-5 flex items-center justify-center focus:outline-none'
                            >
                              <MailIcon className='h-5 w-5 text-white' />
                            </IconContainer>
                          </div>
                          {isMobile && (
                            <a
                              href={`sms:${isIOS ? '&' : '?'}body=${getUrl()}`}
                              onClick={() => {
                                track({ eventId: trackingEvents.ClickToText });
                                trackAnalyticsEvent({
                                  gtagEvent: 'ShareClick',
                                  mouseflowTag: 'Share',
                                });
                              }}
                              aria-label={'Share by text message'}
                            >
                              <IconContainer
                                data-aos='fade-down'
                                data-aos-duration='100'
                                data-aos-delay='50'
                                className='rounded-full bg-mattamy-blue mt-16 flex items-center justify-center focus:outline-none'
                              >
                                <ChatIcon className='h-5 w-5 text-white' />
                              </IconContainer>
                            </a>
                          )}
                        </>
                      )}
                    </ShareIconsContainer>
                  )}
                </div>
              </Header>
              {isMobile && (
                <>
                  <animated.div
                    className='absolute z-30 w-full bg-mattamy-blue flex flex-col justify-center overflow-auto'
                    style={dropdown}
                  >
                    {communityAndPlans &&
                      communityAndPlans.map((item, i) => (
                        <button
                          className={cx(
                            'uppercase tracking-tight leading-none active:outline-none focus:outline-none font-trade-gothic-20 text-white font-semibold pt-1 my-2',
                          )}
                          key={item.id + i}
                          onClick={(e) => {
                            setGalleryFilter(item.id);
                            setResetMedia(true);
                            setOpen(false);
                            setMediaFilter();
                          }}
                        >
                          {item.title}
                        </button>
                      ))}
                  </animated.div>
                  <FiltersMedia
                    className={cx(
                      'absolute top-0 left-0 ml-5 mt-20 z-20 rounded-full flex bg-white uppercase  justify-center items-center text-sm font-trade-gothic-20 cursor-pointer uppercase',
                      {
                        'text-white': showNewSchemaColor,
                        'text-mattamy-blue': !showNewSchemaColor,
                        hidden: !(filteredCtas && filteredCtas.length > 1),
                      },
                    )}
                    role='radiogroup'
                    aria-label='Content type'
                  >
                    {filteredCtas &&
                      filteredCtas.map((item, i) => (
                        <FiltersMediaButton
                          className={cx(
                            'h-full flex flex-row  uppercase border-2  items-center',
                            {
                              'pl-2 rounded-l-full': i === 0,
                              'pr-2 rounded-r-full':
                                i === filteredCtas.length - 1,
                              'border-mattamy-blue bg-mattamy-blue':
                                !showNewSchemaColor &&
                                mediaFilters[item.text] === mediaFilter,
                              'border-mattamy-blue bg-white':
                                !showNewSchemaColor &&
                                mediaFilters[item.text] !== mediaFilter,
                              /* "border-white bg-white":
                                showNewSchemaColor &&
                                mediaFilters[item.text] === mediaFilter,
                              "border-white bg-black":
                                showNewSchemaColor &&
                                mediaFilters[item.text] !== mediaFilter,*/
                            },
                          )}
                          showNewSchemaColor={showNewSchemaColor}
                          isFilterSelected={
                            mediaFilters[item.text] === mediaFilter
                          }
                          newSchemaColorHexaCode={newSchemaColorHexaCode}
                          newSchemaTextColorHexaCode={
                            newSchemaTextColorHexaCode
                          }
                          key={item.text + i}
                          isTour={item.text === '360 Tours'}
                          role='radio'
                          aria-checked={mediaFilters[item.text] === mediaFilter}
                          aria-label={item.text}
                          onClick={(e) => {
                            if (mediaFilters[item.text] === mediaFilter) {
                              setMediaFilter();
                            } else setMediaFilter(mediaFilters[item.text]);
                            setOpen(false);
                            setChekBoxFocusTitle(item.text);
                          }}
                        >
                          <FilterMediaIconContainer
                            className={cx('pl-2 flex w-8', {
                              'text-white':
                                !showNewSchemaColor &&
                                mediaFilters[item.text] == mediaFilter,
                              'text-white':
                                showNewSchemaColor &&
                                mediaFilters[item.text] !== mediaFilter,
                              'text-mattamy-blue':
                                !showNewSchemaColor &&
                                mediaFilters[item.text] !== mediaFilter,
                            })}
                            showNewSchemaColor={showNewSchemaColor}
                            isFilterSelected={
                              mediaFilters[item.text] === mediaFilter
                            }
                            newSchemaColorHexaCode={newSchemaColorHexaCode}
                            newSchemaTextColorHexaCode={
                              newSchemaTextColorHexaCode
                            }
                          >
                            {item.key === 'video' && (
                              <VideoIconGallery className={'w-8'} />
                            )}
                            {item.key === 'tour' && (
                              <TourIconGallery className={'w-8'} />
                            )}
                            {item.key === 'image' && (
                              <PhotoIconGallery className={'w-8'} />
                            )}
                          </FilterMediaIconContainer>

                          <FilterMediaTextContainer
                            className={cx('ml-1 mt-1 pr-1', {
                              'text-mattamy-blue':
                                !showNewSchemaColor &&
                                mediaFilters[item.text] !== mediaFilter,
                              'text-white':
                                !showNewSchemaColor &&
                                mediaFilters[item.text] === mediaFilter,
                              'text-white':
                                showNewSchemaColor &&
                                mediaFilters[item.text] !== mediaFilter,
                              /* "text-black":
                                showNewSchemaColor &&
                                mediaFilters[item.text] === mediaFilter,*/
                            })}
                            showNewSchemaColor={showNewSchemaColor}
                            isFilterSelected={
                              mediaFilters[item.text] === mediaFilter
                            }
                            newSchemaColorHexaCode={newSchemaColorHexaCode}
                            newSchemaTextColorHexaCode={
                              newSchemaTextColorHexaCode
                            }
                          >
                            {item.text}
                          </FilterMediaTextContainer>
                        </FiltersMediaButton>
                      ))}
                  </FiltersMedia>
                </>
              )}
              {/* <AspectRatio
                ratio={isMobile ? '375/310' : '1200/650'}
                className='w-full'
              >
              </AspectRatio> */}
              {filteredMedia && (
                <StyledImageCarousel
                  isMobile={isMobile}
                  carousel={filteredMedia}
                  fullWidth
                  counterRight={true}
                  noArrows={isMobile}
                  isContainerRounded={false}
                  reset={resetMedia}
                  onChangeReset={handleReset}
                  descriptionColor={'white'}
                  communityGallery
                  backgroundColor={'light-blue'}
                  mobileAspectRatio='375/310'
                  desktopAspectRatio='1200/650'
                />
              )}
            </StaticCarouselContainer>
          </div>
          {filteredMedia && (
            <div className='w-full mt-2 flex flex-col'>
              {renderVideoDescription()}
            </div>
          )}
        </Container>
      </StyledSection>
    </>
  );
};

export default GalleryComponent;
